import {createContext, useContext, ReactNode, useMemo} from 'react';

export const FileUploadUnavailableError = new Error('File upload unavailable');

export class FileUploadValidationError extends Error {
  file?: File;

  code?: string;

  constructor({
    message,
    file,
    code,
  }: {
    message: string;
    file?: File;
    code?: string;
  }) {
    super(message);
    this.file = file;
    this.code = code;
  }
}

export const DEFAULT_SUPPORTED_MIME_TYPES: string[] = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'text/plain',
  'text/xml',
  'text/csv',
  'text/tab-separated-values',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const DEFAULT_MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB
export const DEFAULT_MAX_FILE_NAME_LENGTH = 200;

export function defaultValidateFile(file: File) {
  const {size, type, name} = file;

  if (!DEFAULT_SUPPORTED_MIME_TYPES.includes(type)) {
    throw new FileUploadValidationError({
      message: 'File type is not supported',
      file,
      code: 'upload_type_not_supported',
    });
  }

  if (name.length > DEFAULT_MAX_FILE_NAME_LENGTH) {
    throw new FileUploadValidationError({
      message: 'File name is too long',
      file,
      code: 'file_name_too_long',
    });
  }

  if (size > DEFAULT_MAX_FILE_SIZE_BYTES) {
    throw new FileUploadValidationError({
      message: 'File is too large',
      file,
      code: 'file_too_large',
    });
  }
}

export interface FileUploader {
  // available is true if the file uploader is available
  available: boolean;

  // upload takes a File and returns a Promise that resolves
  // to a string token id for the uploaded file.
  // if there are errors, throws
  upload: (
    file: File,
    {abortSignal}: {abortSignal?: AbortSignal},
  ) => Promise<string>;

  // validateFile takes a File and throws a FileUploadValidationError
  validateFile: (file: File) => void;
}

export type FileUploaderValue =
  | {
      available: true;
      upload: (
        file: File,
        {abortSignal}: {abortSignal?: AbortSignal},
      ) => Promise<string>;
      validateFile?: (file: File) => void;
    }
  | {
      available: false;
    };

const defaultFileUploader: FileUploader = {
  available: false,
  upload: async () => {
    throw FileUploadUnavailableError;
  },
  validateFile: defaultValidateFile,
};

const FileUploaderContext = createContext<FileUploader>(defaultFileUploader);

export function FileUploaderProvider({
  value,
  children,
}: {
  value: FileUploaderValue;
  children: ReactNode;
}) {
  const fileUploader = useMemo(() => {
    const {available} = value;
    let impl = defaultFileUploader;
    if (available) {
      const {upload, validateFile} = value;
      impl = {
        available: true,
        upload,
        validateFile: validateFile ?? defaultValidateFile,
      };
    }
    return impl;
  }, [value]);

  return (
    <FileUploaderContext.Provider value={fileUploader}>
      {children}
    </FileUploaderContext.Provider>
  );
}

export function useFileUploader() {
  return useContext(FileUploaderContext);
}
